import { FC, useState } from 'react';
import { cn } from '@shadcn/ui/lib/utils';
import { Card } from '@shadcn/ui/components/ui/card';
import { ContactAvatar } from '../../../../../components/contact-avatar';
import { Sparkles, ChevronLeft, Target } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { GetContactsWeightedScoresQuery, GET_CONTACTS_WEIGHTED_SCORES } from '@zaplify/graphql';
import { ActionType } from '@andsend/intelligence-shared';
import { formatDistanceToNow, subDays } from 'date-fns';
import { htmlToText } from 'html-to-text';

import { QuickActionsBar } from '../../../../../components/buttons';
import { ContactTypeBadge } from '../../../../../components/contact-type-badge';
import { useApolloClient } from '@apollo/client';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui';

type ContactScoreData = GetContactsWeightedScoresQuery['contacts_weighted_scores'][number];

type ActionCardType =
    | 'message' // SEND_EMAIL, SEND_LINKEDIN_MESSAGE, SEND_FOLLOW_UP
    | 'phone' // PHONE_CALL, SCHEDULE_CALL
    | 'meeting' // MEETING, SCHEDULE_MEETING
    | 'other'; // Any other action type

type ActionToCardTypeMap = {
    SEND_EMAIL: 'message';
    SEND_LINKEDIN_MESSAGE: 'message';
    SEND_FOLLOW_UP: 'message';
    PHONE_CALL: 'phone';
    SCHEDULE_CALL: 'phone';
    MEETING: 'meeting';
    SCHEDULE_MEETING: 'meeting';
};

// Helper function to actually perform the mapping
export const mapActionToCardType = (actionType: ActionType): ActionCardType => {
    return (actionType as keyof ActionToCardTypeMap) in actionToCardTypeMap
        ? actionToCardTypeMap[actionType as keyof ActionToCardTypeMap]
        : 'other';
};

const actionToCardTypeMap: ActionToCardTypeMap = {
    SEND_EMAIL: 'message',
    SEND_LINKEDIN_MESSAGE: 'message',
    SEND_FOLLOW_UP: 'message',
    PHONE_CALL: 'phone',
    SCHEDULE_CALL: 'phone',
    MEETING: 'meeting',
    SCHEDULE_MEETING: 'meeting',
};

export const ActionCard: FC<{
    contact: ContactScoreData;
    score: number;
    openSnoozeDialogForContact: (contactFirstName: string, contactId: string) => void;
    openClassificationDialogForContact: (contactId: string) => void;
    condensed?: boolean;
    className?: string;
}> = ({
    contact,
    score,
    openSnoozeDialogForContact,
    openClassificationDialogForContact,
    condensed = false,
    className,
}) => {
    const pageContactId = useParams().contactId;
    const [searchParams] = useSearchParams();
    const threadId = searchParams.get('threadId');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const apolloClient = useApolloClient();

    const handleDropdownStateChange = (isOpen: boolean) => {
        setIsDropdownOpen(isOpen);
    };

    const handleContactTypeUpdated = () => {
        // Refetch the contacts data to update the UI
        apolloClient.refetchQueries({
            include: [GET_CONTACTS_WEIGHTED_SCORES],
        });
    };

    return (
        <Link
            to={{
                pathname: `/new/actions-v2/${mapActionToCardType(contact.topActionType as ActionType)}/${contact.id}`,
                search: threadId ? `?threadId=${threadId}` : '',
            }}
            className="w-full max-w-[min(70dvw,60rem)] min-w-0 flex items-center gap-2 group/action-card"
        >
            <Card
                className={cn(
                    'flex flex-col w-full transition-all duration-100 cursor-pointer relative gap-4 p-4 pb-8',
                    {
                        'bg-background-brand-primary-subtle/40': pageContactId === contact.id,
                    },
                    className
                )}
            >
                {/* Contact Type Badge - Top Right */}
                {!pageContactId && (
                    <div className="absolute top-3 right-3">
                        <ContactTypeBadge
                            contactId={contact.id}
                            contactType={contact.contactType}
                            currentMetadata={contact.metadata}
                            onContactTypeUpdated={handleContactTypeUpdated}
                        />
                    </div>
                )}

                {/* Quick Actions - Bottom Right (Hover Only) */}
                <div
                    className={cn(
                        'absolute bottom-1 right-2 transition-opacity duration-100 z-10',
                        isDropdownOpen ? 'opacity-100' : 'opacity-0 group-hover/action-card:opacity-100'
                    )}
                >
                    <QuickActionsBar
                        contact={contact}
                        openClassificationDialogForContact={openClassificationDialogForContact}
                        variant="ghost"
                        size="icon-sm"
                        className="text-gray-400 gap-0.5"
                        onDropdownStateChange={handleDropdownStateChange}
                    />
                </div>

                {/* Main Content */}
                <div className="flex gap-2 items-start min-w-0">
                    <div
                        className={cn('flex-shrink-0', {
                            'self-start': pageContactId,
                        })}
                    >
                        <ContactAvatar
                            fullName={contact.fullName}
                            avatarUrl={contact.linkedinProfileImgUrl}
                            size="lg"
                        />
                    </div>
                    <div className="flex flex-col gap-1 w-full min-w-0 transition-all duration-150 ease-out">
                        {pageContactId ? (
                            // Compact layout when this contact is selected
                            <div className="flex flex-col gap-0.5 w-full min-w-0">
                                {/* Row 1: Name, Company */}
                                <div className="flex gap-1.5 items-center justify-between min-w-0">
                                    <div className="flex gap-1.5 items-center min-w-0 overflow-hidden">
                                        <p className="text-sm font-medium capitalize flex-shrink-0">
                                            {contact.firstName} {contact.lastName}
                                        </p>
                                        <p className="text-sm text-gray-500 flex-shrink-0">•</p>
                                        <p className="text-sm text-gray-500 capitalize truncate">
                                            {contact.organizationName || contact.linkedinHeadline}
                                        </p>
                                    </div>
                                </div>
                                {/* Message content under name for compact */}
                                {contact.Messages?.[0]?.content ? (
                                    <div className="flex flex-row gap-1 items-center justify-between">
                                        <div className="text-sm line-clamp-1 break-all">
                                            <span className="font-medium">
                                                {contact.Messages?.[0]?.direction === 'out' && 'You: '}
                                            </span>
                                            {htmlToText(contact.Messages?.[0]?.content)}
                                        </div>
                                        {!contact.recommendedActions?.[0]?.actionTags &&
                                            contact.Messages?.[0]?.sentOn && (
                                                <div className="text-sm text-gray-500 flex-shrink-0">
                                                    {formatDistanceToNow(
                                                        new Date(
                                                            contact.Messages?.[0]?.sentOn || subDays(new Date(), 1)
                                                        )
                                                    )}{' '}
                                                    ago
                                                </div>
                                            )}
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">Start a conversation</div>
                                )}
                            </div>
                        ) : (
                            // Original layout when contact is not selected
                            <div className="flex flex-col gap-1 w-full min-w-0">
                                <div className="flex gap-2 items-center justify-between min-w-0">
                                    <div className="flex gap-2 items-center min-w-0 overflow-hidden mr-28">
                                        <p className="text-md sm:text-sm font-medium capitalize flex-shrink-0">
                                            {contact.firstName} {condensed ? '' : contact.lastName}
                                        </p>
                                        <p className="text-sm text-gray-500 flex-shrink-0">•</p>
                                        <p className="text-sm text-gray-500 capitalize truncate">
                                            {contact.organizationName || contact.linkedinHeadline || ''}
                                        </p>
                                    </div>
                                </div>
                                {/* Message content under name for big cards */}
                                {contact.Messages?.[0]?.content && (
                                    <div className="flex gap-1 justify-between w-full">
                                        <span className="text-sm line-clamp-1 max-w-[85%] break-all">
                                            {contact.Messages?.[0]?.direction === 'in'
                                                ? contact.firstName.charAt(0).toUpperCase() +
                                                  contact.firstName.slice(1) +
                                                  ':'
                                                : contact.Messages?.[0]?.direction === 'out'
                                                ? 'You:'
                                                : ''}{' '}
                                            {htmlToText(contact.Messages?.[0]?.content)}
                                        </span>
                                        {!contact.recommendedActions?.[0]?.actionTags &&
                                            contact.Messages?.[0]?.sentOn && (
                                                <span className="flex text-sm text-gray-500 flex-shrink-0">
                                                    {formatDistanceToNow(
                                                        new Date(
                                                            contact.Messages?.[0]?.sentOn || subDays(new Date(), 1)
                                                        )
                                                    )}{' '}
                                                    ago
                                                </span>
                                            )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Agent Analysis - Bottom for big cards */}
                {contact.recommendedActions?.[0]?.actionTags && (
                    <div className="w-full flex justify-between">
                        <Tooltip delayDuration={1000}>
                            <TooltipTrigger asChild>
                                <div className="flex gap-2 rounded w-fit flex-wrap">
                                    {contact.recommendedActions?.[0]?.actionTags.map((tag, index) => (
                                        <Badge
                                            key={tag}
                                            variant="outline"
                                            className="text-xs gap-1 py-1 border-none bg-background-brand-secondary/10 capitalize"
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="center" className="flex gap-2">
                                <Sparkles className="w-4 h-4 flex-shrink-0 mt-1" />
                                <p>{contact.recommendedActions?.[0]?.reason}</p>
                            </TooltipContent>
                        </Tooltip>
                        {contact.Messages?.[0]?.sentOn && (
                            <span className="flex text-sm text-gray-500 flex-shrink-0">
                                {formatDistanceToNow(new Date(contact.Messages?.[0]?.sentOn || subDays(new Date(), 1)))}{' '}
                                ago
                            </span>
                        )}
                    </div>
                )}
                {!pageContactId &&
                    contact.recommendedActions?.[0]?.reason &&
                    !contact.recommendedActions?.[0]?.actionTags && (
                        <div className="flex gap-2 rounded p-2 border-t text-sm w-full">
                            <div className="flex gap-2 items-start mt-1">
                                <Sparkles className="w-4 h-4 text-text-brand-secondary flex-shrink-0" />
                            </div>
                            <div className="flex flex-col gap-1 w-full">
                                <div className="flex gap-2 w-full justify-between">
                                    <span className="font-medium text-text-brand-secondary">Agent analysis</span>
                                </div>
                                <span className="text-text-tertiary">{contact.recommendedActions?.[0]?.reason}</span>
                            </div>
                        </div>
                    )}
                {/* Expansion Hint - Outside Card (Hover Only) */}
                {!pageContactId && (
                    <div className="absolute top-0 -right-5 opacity-0 group-hover/action-card:opacity-40 transition-opacity duration-100 pointer-events-none flex-shrink-0 flex h-full items-center">
                        <ChevronLeft className="h-4 w-4 text-gray-500 group-hover/action-card:animate-hover-left-right" />
                    </div>
                )}
            </Card>
        </Link>
    );
};
